import { t } from 'i18next';
import { useState } from 'react';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { OIL_DISPOSAL_SERVICE_NAME } from '../../../config/constants';
import { ReactComponent as Warning } from '../../../icons/warning.svg';
import {
  inputFormatNumber,
  inputFormatNumberMoney,
} from '../../../utils/numberFormatter';
import ButtonsManifest from '../../molecules/ButtonsManifest';

type OilDisposalAndGreaseTrapFormProps = {
  control: Control<any, any>;
  errors: any;
  route: any;
  paidClient: any;
  paymentType: any;
  pricePerGallon?: string;
};

export const OilDisposalAndGreaseTrapForm = ({
  control,
  errors,
  route,
  paidClient,
  paymentType,
  pricePerGallon,
}: OilDisposalAndGreaseTrapFormProps) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [displayValueMoney, setDisplayValueMoney] = useState<string>('');

  return (
    <>
      <div className="flex flex-col gap-6">
        <span>
          <p className="text-sm text-ReyesBlack">{t('manifest.ccapacity')}</p>
          <Controller
            name="container_capacity"
            control={control}
            disabled
            render={({ field }) => (
              <input
                className="w-80 bg-transparent text-sm text-ReyesGrey focus:outline-none"
                type="text"
                id="container_capacity"
                {...field}
                disabled
                style={{
                  appearance: 'none',
                  MozAppearance: 'textfield',
                }}
              />
            )}
          />
          {errors.container_capacity && (
            <>
              <br />
              <span className="text-red-500">
                {errors.container_capacity.message}
              </span>
            </>
          )}
        </span>
        <span>
          <p className="text-sm text-ReyesBlack">{t('manifest.tGallons')}</p>
          <Controller
            name="total_gallons"
            control={control}
            rules={{
              required: `${t('manifest.error2')}`,
              pattern: /^[0-9]*$/,
              maxLength: 8,
            }}
            render={({ field }) => (
              <>
                <input
                  type="number"
                  id="total_gallons"
                  {...field}
                  style={{ display: 'none' }}
                />
                <input
                  className="w-80 border-b-2 border-ReyesGreyLight bg-transparent text-sm text-ReyesGrey ring-ReyesGreen focus:outline-none"
                  type="text"
                  id="total_gallonsDisplay"
                  value={displayValue}
                  maxLength={8}
                  onChange={(e) => {
                    const numberValue = Number(
                      e.target.value.replace(/,/g, ''),
                    );
                    if (!Number.isNaN(numberValue)) {
                      field.onChange(numberValue);
                      setDisplayValue(inputFormatNumber(numberValue));
                    }
                  }}
                  style={{
                    appearance: 'none',
                    MozAppearance: 'textfield',
                  }}
                />
              </>
            )}
          />
          {errors.total_gallons && (
            <span className="ml-2 text-red-500">
              {errors.total_gallons.message}
            </span>
          )}
        </span>
        {route.service === OIL_DISPOSAL_SERVICE_NAME && paidClient && (
          <div className="flex flex-col gap-y-6">
            <div className="">
              <p className="text-sm text-ReyesBlack">
                {t('manifest.pricePerGallon')}
              </p>
              <Controller
                name="pricePerGallon"
                control={control}
                disabled
                render={({ field }) => (
                  <input
                    className="w-full bg-transparent text-sm text-ReyesGrey focus:outline-none"
                    type="text"
                    id="pricePerGallon"
                    {...field}
                    disabled
                    value={
                      field.value ? inputFormatNumberMoney(field.value) : '-'
                    }
                    style={{
                      appearance: 'none',
                      MozAppearance: 'textfield',
                    }}
                  />
                )}
              />
            </div>
            <span>
              <p className="text-sm text-ReyesBlack">
                {t('manifest.amountToPay')}
              </p>
              {pricePerGallon ? (
                <Controller
                  name="pricePaid"
                  control={control}
                  disabled
                  render={({ field }) => (
                    <input
                      className="w-full bg-transparent text-sm text-ReyesGrey focus:outline-none"
                      type="text"
                      id="pricePaid"
                      {...field}
                      disabled
                      value={inputFormatNumberMoney(field.value)}
                      style={{
                        appearance: 'none',
                        MozAppearance: 'textfield',
                      }}
                    />
                  )}
                />
              ) : (
                <>
                  <Controller
                    name="pricePaid"
                    control={control}
                    rules={{
                      required: `${t('manifest.error2')}`,
                      pattern: /^[0-9]*$/,
                      maxLength: 10,
                    }}
                    disabled={!!pricePerGallon}
                    render={({ field }) => (
                      <>
                        <input
                          type="number"
                          id="pricePaid"
                          {...field}
                          disabled={!!pricePerGallon}
                          style={{ display: 'none' }}
                        />
                        <input
                          className="w-full border-b-2 border-ReyesGreyLight bg-transparent text-sm text-ReyesGrey focus:outline-none"
                          type="text"
                          id="pricePaidDisplay"
                          value={displayValueMoney}
                          disabled={!!pricePerGallon}
                          maxLength={10}
                          onChange={(e) => {
                            const stringValue = e.target.value.replace(
                              /[$,]/g,
                              '',
                            );
                            if (!Number.isNaN(Number(stringValue))) {
                              const numberValue = Number(stringValue);
                              field.onChange(numberValue);
                              setDisplayValueMoney(
                                inputFormatNumberMoney(numberValue),
                              );
                            }
                          }}
                          style={{
                            appearance: 'none',
                            MozAppearance: 'textfield',
                          }}
                        />
                      </>
                    )}
                  />
                  {errors.pricePaid && (
                    <>
                      <br />
                      <span className="text-red-500">
                        {errors.pricePaid.message}
                      </span>
                    </>
                  )}
                </>
              )}
            </span>
            {paymentType && paymentType[0] === 'Cash' && (
              <>
                <div className="flex w-full flex-row items-center justify-start gap-x-2 rounded-md bg-ambar px-2 text-center">
                  <Warning fill="#2e4347" className="h-6 w-6" />
                  <p className="my-2 font-inter font-bold text-ReyesGreen">
                    {t('manifest.cashWarning')}
                  </p>
                </div>
                <div>
                  <p className="mt-2 text-sm text-ReyesBlack">
                    {t('manifest.paymentStatus')}
                  </p>
                  <div className="container flex justify-center">
                    <Controller
                      name="paymentStatus"
                      control={control}
                      rules={{ required: `${t('manifest.error1')}` }}
                      render={({ field }) => (
                        <div className="flex-col">
                          <div className="text-center">
                            <ButtonsManifest
                              field={field}
                              options={['Yes', 'No']}
                            ></ButtonsManifest>
                            {errors.paymentStatus && (
                              <span className="text-red-500">
                                {errors.paymentStatus.message}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div>
          <p className="mb-2 text-sm text-ReyesBlack">
            {t('manifest.measuringMethod')}
          </p>
          <div className="container flex justify-center">
            <Controller
              name="measuring_method"
              control={control}
              rules={{ required: `${t('manifest.error1')}` }}
              render={({ field }) => (
                <div className="flex-col">
                  <div className="text-center">
                    <ButtonsManifest
                      field={field}
                      options={['Metered', 'Weighted', 'Mathematical']}
                    ></ButtonsManifest>
                    {errors.measuring_method && (
                      <span className="text-red-500">
                        {errors.measuring_method.message}
                      </span>
                    )}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>
      <div className="w-80">
        <p className="text-sm text-ReyesBlack">{t('manifest.percentage')}</p>
        <div className="flex">
          <Controller
            name="percentage_filled"
            control={control}
            // rules={{
            //   required: `${t('manifest.error2')}`,
            //   pattern: /^[0-9]*$/,
            //   maxLength: {
            //     value: 3,
            //     message: `${t('manifest.error3')}`,
            //   },
            //   validate: (value) =>
            //     parseInt(value, 10) <= 100 ||
            //     `${t('manifest.error4')}`,
            // }}
            disabled
            render={({ field }) => (
              <input
                className="w-full bg-transparent text-sm text-ReyesGrey focus:outline-none"
                type="text"
                id="percentage_filled"
                {...field}
                disabled
                style={{
                  appearance: 'none',
                  MozAppearance: 'textfield',
                }}
              />
            )}
          />
          {/* <p className="self-center text-sm font-semibold text-ReyesGrey">
                      %
                    </p> */}
        </div>
        {errors.percentage_filled && (
          <span className="text-red-500">
            {errors.percentage_filled.message}
          </span>
        )}
      </div>
    </>
  );
};
